import AvailableHelper from "../Availability/AvailableHelper";
import HoursHelper from "../Availability/HoursHelper";
import DateService from "../../../Common/Date/DateService";
import ItemViewHelper from "../Item/ItemViewHelper";
import ItemHelper from "../Item/ItemHelper";
import {ConfigApi, OrderType} from "../../../Api/types";
import {TFunction} from "i18next";
import {getParsedSessionOrderType} from "../../../../utils/order";
import {DateHelper, getHourTimeString} from "../../../Common/Date/DateHelper";

type ParsedCategory = {
    id: any;
    position: number;
    name: string;
    image_link: any;
    description: string;
    entities: any[];
};

class CategoryHelper {
    static getAvailableCategoryItems(availabilities: any[], pickupAt: string | undefined, items: any[]) {
        return items.filter((item: any) => {
            let isDisabled = item.disabled;
            if (item.type === "ITEM_GROUP" && item.item) {
                isDisabled = item.item.disabled ?? false;
            }

            if (isDisabled) return false;

            const itemAvailabilityId = item.type === "ITEM_GROUP" ? item.item?.availability_id : item.availability_id;
            const availability = AvailableHelper.getAvailability(availabilities, itemAvailabilityId);

            if (!availability) {
                return !itemAvailabilityId;
            }

            return HoursHelper.isOpen(availability.hours, DateService.emptyOrNow(pickupAt)) && AvailableHelper.isAvailabile(availability, DateService.emptyOrNow(pickupAt));
        });
    }

    static getMenusForCategory(menus: any[], category: any) {
        return menus.filter((menu: any) => menu.categories.map((menuCategory: any) => menuCategory.category_id).includes(category.id));
    }

    static getCategoryPosition(category: any, menus: any[]) {
        const positions = menus.map((menu: any) => menu.categories.find((menuCategory: any) => menuCategory.category_id === category.id)?.position)
            .filter(position => position || position === 0);

        return positions.length > 0 ? Math.min(...positions) : 0;
    }

    static getParsedCategories(categories: any[], items: any[], availabilities: any[], availablePromotions: any[], session: any, cart: any, itemGroups: any[], menus: any[]): ParsedCategory[] {
        return categories.filter((category: any) => category.status === 'ENABLED').map((category: any) => {
            const newCategory: ParsedCategory = {
                id: category.id,
                position: this.getCategoryPosition(category, menus),
                name: category.translation.name,
                image_link: category.image_link,
                description: category.translation.description,
                entities: []
            };


            newCategory.entities = category.entities.map((entity: any) => {
                if (entity.type === "ITEM") {
                    const item = items.find((item: any) => item.id === entity.entity_id);

                    if (!item) return null;

                    return ItemViewHelper.parseItem(item, availabilities, availablePromotions, session.pickup_at, entity.position, cart, session.order_type);
                }
            }).filter(Boolean);

            if (itemGroups && itemGroups.length > 0) {
                newCategory.entities = newCategory.entities.map((entity: any) => {
                    const itemGroupForItem = ItemHelper.getItemGroup(itemGroups, entity.id);

                    if (!itemGroupForItem) return entity;
                    return ItemViewHelper.parseItemGroup(itemGroupForItem, items, availabilities, availablePromotions, session.pickup_at, entity.position, cart, session.order_type);
                }).reduce((acc: any[], entity: any) => {
                    if (!entity.reference_id) {
                        acc.push(entity);
                        return acc;
                    }

                    const existing = acc.find(x => x.reference_id === entity.reference_id);
                    if (existing) {
                        if (entity.position < existing.position) existing.position = entity.position;
                    } else {
                        acc.push(entity);
                    }
                    return acc;
                }, []);
            }

            return newCategory.entities.length > 0 ? newCategory : null;
        }).filter((category): category is ParsedCategory => category !== null);
    }

    static getSortedCategoryItems(category: any): any[] {
        return [...category.entities].sort((a: any, b: any) => a.position - b.position || a.name.localeCompare(b.name));
    }

    static getAvailabilityInfo(category: any, menus: any[], items: any[], availabilities: any[],
                               config: ConfigApi | undefined, orderType: OrderType, pickupDate: string | undefined,
                               t: TFunction, locale?: string, startFormattedDateWithUpperCase?: boolean): {
        text: string;
        typesText?: string;
        dateText?: string;
        currentType?: boolean;
        isNowAvailable?: boolean;
        includeCurrentType?: boolean
    }[] {
        const menuAvailabilitiesForOrderTypes = Object.keys(OrderType).reduce((acc: any, type: string) => {
            const menusWithCategory = CategoryHelper.getMenusForCategory(menus, category);
            const menusWithCategoryForOrderType = menusWithCategory.filter((menu: any) => menu[getParsedSessionOrderType(type).toLowerCase()] === true);
            const menuAvailabilitiesForType = AvailableHelper.getAvailabilities(availabilities, menusWithCategoryForOrderType.map((menu: any) => menu.availability_id));
            const maxSearchDate = DateService.emptyOrNow(pickupDate);
            maxSearchDate.setDate(maxSearchDate.getDate() + 30);
            const date = AvailableHelper.getNearestOpenDateFromNowOrDate(menuAvailabilitiesForType, DateService.emptyOrNow(pickupDate), true, maxSearchDate);
            acc[type] = {
                availabilities: menuAvailabilitiesForType,
                date,
            }
            return acc;
        }, {});

        const storeOpenHours = config?.hours || [];

        const orderTypesAvailabilities = (Object.keys(OrderType) as OrderType[]).reduce((acc: any, type) => {
            if (items.every((item: any) => {
                const itemOrderTypes = (item.item ?? item).order_types;

                if (itemOrderTypes.length > 0) {
                    return !itemOrderTypes.includes(type);
                }

                return false;
            })) {
                acc[type] = undefined;

                return acc;
            }

            const anyItemAlwaysAvailable = items.some((item: any) => item.item_always_available);
            const itemsWithRestrictions = items.filter(i => !i.item_always_available)
                .sort((a: any, b: any) => a.item_nearest_available_date_per_type[type]?.dateFrom?.getTime() - b.item_nearest_available_date_per_type[type]?.dateFrom?.getTime())
            const menuAvailabilitiesForType = menuAvailabilitiesForOrderTypes[type];
            let nearestAvailableDate = undefined;
            let searchDate = DateService.emptyOrNow(pickupDate);

            const maxSearchTries = 30;
            let currentSearchTry = 0;
            while (!nearestAvailableDate && itemsWithRestrictions.length && !anyItemAlwaysAvailable) {
                if (currentSearchTry >= maxSearchTries) break;

                for (const itemsWithRestrictionsElement of itemsWithRestrictions) {
                    const item = itemsWithRestrictionsElement?.item || itemsWithRestrictionsElement;
                    const maxSearchDate = new Date(searchDate);
                    maxSearchDate.setDate(maxSearchDate.getDate() + 30);
                    const nextItemAvailableDate = ItemHelper.getNearestAvailabilityDateForItem(item, availabilities, searchDate ? DateHelper.format(searchDate) : undefined, type, maxSearchDate);

                    if (nextItemAvailableDate.dateFrom) {
                        AvailableHelper.trimHoursToAvailabilities(menuAvailabilitiesForType.availabilities, nextItemAvailableDate);
                        HoursHelper.trimHoursToStore(storeOpenHours, nextItemAvailableDate);
                    }

                    if (nextItemAvailableDate.dateTo?.getTime() === nextItemAvailableDate.dateFrom?.getTime()) {
                        searchDate.setDate(searchDate.getDate() + 1);
                        searchDate.setHours(0, 0, 0, 0);
                        currentSearchTry++;
                        continue;
                    }

                    if (nextItemAvailableDate.dateFrom
                        && AvailableHelper.checkIfDateIsInStoreAndMenu(storeOpenHours, menuAvailabilitiesForType.availabilities, nextItemAvailableDate.dateFrom)
                        && searchDate.getTime() <= nextItemAvailableDate.dateFrom.getTime()
                    ) {
                        nearestAvailableDate = nextItemAvailableDate;
                        break;
                    } else if (nextItemAvailableDate.dateTo
                        && AvailableHelper.checkIfDateIsInStoreAndMenu(storeOpenHours, menuAvailabilitiesForType.availabilities, nextItemAvailableDate.dateTo)
                        && searchDate.getTime() < nextItemAvailableDate.dateTo.getTime()
                    ) {
                        nearestAvailableDate = nextItemAvailableDate;
                        break;
                    }
                }

                searchDate.setDate(searchDate.getDate() + 1);
                searchDate.setHours(0, 0, 0, 0);
                currentSearchTry++;
            }

            if (!nearestAvailableDate) {
                currentSearchTry = 0;
                searchDate = DateService.emptyOrNow(pickupDate);
                while (!nearestAvailableDate && (itemsWithRestrictions.length === 0 || anyItemAlwaysAvailable)) {
                    if (currentSearchTry >= maxSearchTries) break;
                    for (const availability of menuAvailabilitiesForType.availabilities) {
                        const availabilityDate = new Date(searchDate);
                        availabilityDate.setHours(0, 0, 0, 0)
                        const date: { dateFrom: Date; dateTo?: Date } = {
                            dateFrom: new Date(availabilityDate),
                            dateTo: undefined,
                        };
                        AvailableHelper.trimHoursToAvailabilities([availability], date);
                        HoursHelper.trimHoursToStore(storeOpenHours, date);

                        if (date.dateTo?.getTime() === date.dateFrom?.getTime()) {
                            searchDate.setDate(searchDate.getDate() + 1);
                            searchDate.setHours(0, 0, 0, 0);
                            currentSearchTry++;
                            continue;
                        }

                        if (date.dateFrom && AvailableHelper.checkIfDateIsInStoreAndMenu(storeOpenHours, menuAvailabilitiesForType.availabilities, date.dateFrom)
                            && searchDate.getTime() <= date.dateFrom?.getTime()) {
                            nearestAvailableDate = date;
                            break;
                        } else if (date.dateTo) {
                            const compareDateTo = new Date(date.dateTo);
                            if (compareDateTo <= date.dateFrom) {
                                compareDateTo.setDate(compareDateTo.getDate() + 1);
                            }
                            if (AvailableHelper.checkIfDateIsInStoreAndMenu(storeOpenHours, menuAvailabilitiesForType.availabilities, date.dateFrom)
                                && searchDate.getTime() < compareDateTo.getTime()) {
                                nearestAvailableDate = date;
                                break;
                            }
                        }
                    }

                    searchDate.setDate(searchDate.getDate() + 1);
                    searchDate.setHours(0, 0, 0, 0);
                    currentSearchTry++;
                }
            }

            acc[type] = nearestAvailableDate;

            return acc;
        }, {});

        const allTypesAvailabilities = Object.keys(orderTypesAvailabilities)
            .map(type => {
                if (!orderTypesAvailabilities[type]) return undefined;

                return orderTypesAvailabilities[type];
            })
            .filter(Boolean)
            .sort((a, b) => a.dateFrom.getTime() - b.dateFrom.getTime());

        let infos: {
            text: string;
            typesText?: string;
            dateText?: string;
            currentType?: boolean;
            isNowAvailable?: boolean;
            includeCurrentType?: boolean;
        }[] = [];

        const otherTypesDateGroups: {
            types: string[];
            dateFrom: Date;
            dateTo: Date;
        }[] = [];
        if (allTypesAvailabilities.length) {
            Object.keys(orderTypesAvailabilities)
                .forEach(type => {
                    const orderTypesAvailability = orderTypesAvailabilities[type];
                    if (orderTypesAvailability) {
                        const index = otherTypesDateGroups.findIndex(group => group?.dateFrom.getTime() === orderTypesAvailability?.dateFrom.getTime() && group.dateTo?.getTime() === orderTypesAvailability?.dateTo.getTime())
                        if (index > -1) {
                            otherTypesDateGroups[index].types.push(type);
                        } else {
                            otherTypesDateGroups.push({
                                types: [type],
                                dateFrom: orderTypesAvailability.dateFrom,
                                dateTo: orderTypesAvailability.dateTo,
                            })
                        }
                    }
                });

            otherTypesDateGroups.forEach(group => {
                infos.push({
                    text: `${t("modules.menu.field.available.title")}:`,
                    typesText: group.types.map(type => t(`enums.order.type.${type}`)).join(", "),
                    dateText: `${DateHelper.getFormattedNearestDate(DateHelper.format(group.dateFrom), t, locale ?? "", startFormattedDateWithUpperCase)} - ${getHourTimeString(group.dateTo)}`,
                    isNowAvailable: group.dateFrom && group.dateTo && new Date() >= group.dateFrom && new Date() < group.dateTo,
                    currentType: group.types.length === 1 && group.types[0] === orderType,
                    includeCurrentType: group.types.includes(orderType),
                })
            });
        }

        return infos.length > 0 ? infos : [{text: t("common.word.not_available")}];
    };
}

export default CategoryHelper;